import {
  Backdrop,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  TextField,
} from "@material-ui/core";
import { Cached } from "@material-ui/icons";
import { format as formatTZ, toDate } from "date-fns-tz";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { graphType } from "../../../model/Common";
import {
  chartType,
  dataGraphSelectLabel,
  languageType,
  rangeType,
} from "../../../model/DataGraph";
import {
  selector as dataGraphStoreSelector,
  fetchCreatePDF,
  fetchCreateStart,
  fetchListProductDailies,
  fetchListProductLastMonths,
  fetchListShotDailies,
  fetchListShotLastMonths,
} from "../../../store/DataGraphStore";
import {
  fetchListOfficeInfos,
  fetchUpdateSelectedOfficeInfo,
  selector as officeStoreSelector,
} from "../../../store/OfficeStore";
import {
  fetchListReferenceLines,
  selector as referenceLineStoreSelector,
} from "../../../store/ReferenceLineStore";
import {
  Chart,
  ChartTable,
  DateTextField,
  ProgressView,
  SelectOfficeField,
  SelectTextField,
} from "../../share";
import useStyles from "../useStyles";

const iconColor = "inherit";
const iconSize = "large";
const iconEdge = "end";

/**
 * DataGraphCardコンポーネント.
 *
 * @return {object} コンポーネント
 */
function DataGraphCard() {
  // スタイル
  const styles = useStyles();

  // 表示言語
  const language = languageType.ja;

  // Dispatch
  const dispatch = useDispatch();

  const {
    // 拠点情報一覧
    officeInfos,
    // 選択されている拠点情報
    selectedOfficeInfo,
  } = useSelector(officeStoreSelector);

  const {
    // 製品単位データ一覧
    productDatas,
    // Shot数単位データ一覧
    shotDatas,
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: isDataGraphLoading,
    // PDF生成中フラグ（true: 表示、false: 非表示）
    isCreating,
  } = useSelector(dataGraphStoreSelector);

  const {
    // 読み込み中フラグ（true: 表示、false: 非表示）
    isLoading: isReferenceLineLoading,
  } = useSelector(referenceLineStoreSelector);

  // 選択済み日付
  const [dateSelect, setDateSelect] = React.useState(
    formatTZ(toDate(new Date()), "yyyy-MM-dd", { timeZone: "Asia/Tokyo" })
  );
  // 表示期間の選択状態State
  const [rangeSelect, setRangeSelect] = React.useState(rangeType[0]);
  // 表示グラフの選択状態State
  const [graphSelect, setGraphSelect] = React.useState(chartType[0]);
  const [fileName, setFileName] = React.useState(
    "拠点番号-拠点名_YYYY-mm-dd_表示項目_日付範囲"
  );
  // Chart -> PNG 変換用
  const refX = React.useRef();
  const refY = React.useRef();

  // 副作用
  React.useEffect(() => {
    dispatch(fetchListOfficeInfos());
  }, [dispatch]);

  // 副作用
  React.useEffect(() => {
    if (selectedOfficeInfo.serialNumber) {
      // グラフ線図データ取得
      dispatch(fetchListReferenceLines(selectedOfficeInfo.serialNumber));

      if (graphSelect.value === chartType[1].value) {
        if (rangeSelect.value === rangeType[1].value) {
          // 製品単位データ(日単位)取得
          dispatch(
            fetchListProductDailies(selectedOfficeInfo.serialNumber, dateSelect)
          );
        } else if (rangeSelect.value === rangeType[0].value) {
          // 製品単位データ(月単位)取得
          dispatch(
            fetchListProductLastMonths(
              selectedOfficeInfo.serialNumber,
              dateSelect
            )
          );
        }
      } else if (graphSelect.value === chartType[0].value) {
        if (rangeSelect.value === rangeType[1].value) {
          // Shot数単位データ(日単位)取得
          dispatch(
            fetchListShotDailies(selectedOfficeInfo.serialNumber, dateSelect)
          );
        } else if (rangeSelect.value === rangeType[0].value) {
          // Shot数単位データ(月単位)取得
          dispatch(
            fetchListShotLastMonths(selectedOfficeInfo.serialNumber, dateSelect)
          );
        }
      }
    }
  }, [
    dispatch,
    selectedOfficeInfo.serialNumber,
    graphSelect.value,
    rangeSelect.value,
    dateSelect,
  ]);

  React.useEffect(() => {
    let office = "拠点番号-拠点名";
    if (selectedOfficeInfo.serialNumber) {
      office = `${selectedOfficeInfo.serialNumber}-${selectedOfficeInfo.label}`;
    }
    setFileName(
      `${office}_${dateSelect || "YYYY-mm-dd"}_${
        graphSelect.label[languageType.en].replaceAll(/\s+/g, "") ||
        "グラフ種別"
      }_${rangeSelect.value || "日付範囲"}`
    );
  }, [
    selectedOfficeInfo.serialNumber,
    selectedOfficeInfo.label,
    dateSelect,
    graphSelect.label,
    rangeSelect.value,
    language,
  ]);

  React.useEffect(() => {
    if (isCreating) {
      dispatch(
        fetchCreatePDF(
          fileName,
          `${selectedOfficeInfo.serialNumber}-${selectedOfficeInfo.label}`,
          dateSelect,
          rangeSelect,
          graphSelect,
          refX,
          refY
        )
      );
    }
  }, [
    dispatch,
    isCreating,
    fileName,
    selectedOfficeInfo.serialNumber,
    selectedOfficeInfo.label,
    dateSelect,
    rangeSelect,
    graphSelect,
    refX,
    refY,
  ]);

  // リロードボタン押下時の処理
  const getGraphData = () => {
    if (selectedOfficeInfo.serialNumber) {
      // グラフ線図データ取得
      dispatch(fetchListReferenceLines(selectedOfficeInfo.serialNumber));

      if (graphSelect.value === chartType[1].value) {
        if (rangeSelect.value === rangeType[1].value) {
          // 製品単位データ(日単位)取得
          dispatch(
            fetchListProductDailies(selectedOfficeInfo.serialNumber, dateSelect)
          );
        } else if (rangeSelect.value === rangeType[0].value) {
          // 製品単位データ(月単位)取得
          dispatch(
            fetchListProductLastMonths(
              selectedOfficeInfo.serialNumber,
              dateSelect
            )
          );
        }
      } else if (graphSelect.value === chartType[0].value) {
        if (rangeSelect.value === rangeType[1].value) {
          // Shot数単位データ(日単位)取得
          dispatch(
            fetchListShotDailies(selectedOfficeInfo.serialNumber, dateSelect)
          );
        } else if (rangeSelect.value === rangeType[0].value) {
          // Shot数単位データ(月単位)取得
          dispatch(
            fetchListShotLastMonths(selectedOfficeInfo.serialNumber, dateSelect)
          );
        }
      }
    }
  };

  // 拠点名(SelectOfficeField)の項目変更時の処理
  const handleSelectChange = (event) => {
    if (event && event.target && event.target.value) {
      dispatch(fetchUpdateSelectedOfficeInfo(event.target.value));
    }
  };

  // 日付変更時の処理
  const handleDateChange = (event) => {
    if (event && event.target && event.target.value) {
      setDateSelect(event.target.value);
    }
  };

  // 表示期間(SelectTextField)の項目変更時の処理
  const handleChangeRangeSelect = (event) => {
    if (event && event.target && event.target.value) {
      setRangeSelect(
        rangeType[
          rangeType.findIndex(({ value }) => value === event.target.value)
        ]
      );
    }
  };

  // 表示項目(SelectTextField)の項目変更時の処理
  const handleChangeGraphSelect = (event) => {
    if (event && event.target && event.target.value) {
      setGraphSelect(
        chartType[
          chartType.findIndex(({ value }) => value === event.target.value)
        ]
      );
    }
  };

  const handleChangeFileName = (event) => {
    if (event && event.target && event.target.value) {
      setFileName(event.target.value);
    }
  };

  return (
    <>
      <Card className={styles.card}>
        <CardContent className={`${styles.cardContent} ${styles.flexStart}`}>
          <SelectOfficeField
            label={dataGraphSelectLabel.Customer[language]}
            value={selectedOfficeInfo.serialNumber}
            onChange={handleSelectChange}
            items={officeInfos}
          />
          <div className={styles.marginLeftAuto}>
            <IconButton
              color={iconColor}
              edge={iconEdge}
              disabled={
                !selectedOfficeInfo.serialNumber ||
                isDataGraphLoading ||
                isReferenceLineLoading
              }
              onClick={() => {
                dispatch(fetchListOfficeInfos());
                getGraphData();
              }}
            >
              <Cached fontSize={iconSize} />
            </IconButton>
          </div>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <DateTextField
            label={dataGraphSelectLabel.Date[language]}
            value={dateSelect}
            type="date"
            onChange={handleDateChange}
          />
          <SelectTextField
            label={dataGraphSelectLabel.Range[language]}
            variant="standard"
            value={rangeSelect.value}
            onChange={handleChangeRangeSelect}
            items={rangeType}
            language={language}
          />
        </CardContent>
        <CardContent className={styles.cardContent}>
          <SelectTextField
            label={dataGraphSelectLabel.DisplayItem[language]}
            variant="outlined"
            value={graphSelect.value}
            onChange={handleChangeGraphSelect}
            items={chartType}
            language={language}
          />
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Grid className={styles.reportGrid} container spacing={1}>
            <Grid className={styles.reportGridItemLarge} item>
              <TextField
                className={styles.reportInputTextField}
                id="pdf-name"
                type="text"
                label="ファイル名"
                InputLabelProps={{
                  shrink: true,
                }}
                value={fileName}
                variant="standard"
                onChange={handleChangeFileName}
              />
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchCreateStart(languageType.ja))}
              >
                JPNファイル出力
              </Button>
            </Grid>
            <Grid className={styles.reportGridItemSmall} item>
              <Button
                className={styles.reportInputButton}
                variant="contained"
                color="primary"
                onClick={() => dispatch(fetchCreateStart(languageType.en))}
              >
                ENGファイル出力
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <CardContent className={styles.cardContent}>
          <Grid container spacing={0} style={{ width: "100%" }}>
            <Grid item style={{ width: "50%" }}>
              <Chart
                ref={refX}
                chart={graphSelect.value}
                range={rangeSelect.value}
                type={graphType.lr}
              />
            </Grid>
            <Grid item style={{ width: "50%" }}>
              <Chart
                ref={refY}
                chart={graphSelect.value}
                range={rangeSelect.value}
                type={graphType.fb}
              />
            </Grid>
            <Grid item style={{ width: "50%", padding: 20 }}>
              {graphSelect.value === chartType[1].value ? (
                <ChartTable
                  chart={graphSelect.value}
                  type={graphType.lr}
                  data={
                    rangeSelect.value === rangeType[1].value
                      ? productDatas.daily.x
                      : productDatas.month.x
                  }
                />
              ) : (
                <ChartTable
                  chart={graphSelect.value}
                  type={graphType.lr}
                  data={
                    rangeSelect.value === rangeType[1].value
                      ? shotDatas.daily.x
                      : shotDatas.month.x
                  }
                />
              )}
            </Grid>
            <Grid item style={{ width: "50%", padding: 20 }}>
              {graphSelect.value === chartType[1].value ? (
                <ChartTable
                  chart={graphSelect.value}
                  type={graphType.fb}
                  data={
                    rangeSelect.value === rangeType[1].value
                      ? productDatas.daily.y
                      : productDatas.month.y
                  }
                />
              ) : (
                <ChartTable
                  chart={graphSelect.value}
                  type={graphType.fb}
                  data={
                    rangeSelect.value === rangeType[1].value
                      ? shotDatas.daily.y
                      : shotDatas.month.y
                  }
                />
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Backdrop
        className={styles.backdrop}
        open={isDataGraphLoading || isReferenceLineLoading || isCreating}
      >
        <ProgressView />
      </Backdrop>
    </>
  );
}

export default DataGraphCard;
